import { supabase } from '../lib/supabase';

/**
 * Service for managing tools and accessories records with photo uploads
 */
export const toolsAccessoriesService = {
  /**
   * Upload photo to storage bucket
   * @param {File} file - Photo file
   * @param {string} workOrderId - Work order ID for organizing files
   * @param {string} category - Category name
   * @returns {Promise<string>} - Storage URL
   */
  async uploadPhoto(file, workOrderId, category) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${workOrderId}/${category}/${Date.now()}.${fileExt}`;

      const { data, error } = await supabase?.storage?.from('tools-accessories-photos')?.upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      return data?.path;
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw error;
    }
  },

  /**
   * Get signed URL for private photo
   * @param {string} path - Storage path
   * @returns {Promise<string>} - Signed URL
   */
  async getPhotoUrl(path) {
    try {
      const { data, error } = await supabase?.storage?.from('tools-accessories-photos')?.createSignedUrl(path, 3600); // 1 hour expiry

      if (error) throw error;

      return data?.signedUrl;
    } catch (error) {
      console.error('Error getting photo URL:', error);
      throw error;
    }
  },

  /**
   * Delete photo from storage
   * @param {string} path - Storage path
   */
  async deletePhoto(path) {
    try {
      const { error } = await supabase?.storage?.from('tools-accessories-photos')?.remove([path]);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting photo:', error);
      throw error;
    }
  },

  /**
   * Create tools/accessories record
   * @param {Object} data - Record data
   * @returns {Promise<Object>} - Created record
   */
  async create(data) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      const recordData = {
        work_order_id: data?.workOrderId,
        category: data?.category,
        item_name: data?.itemName,
        quantity: data?.quantity || 1,
        photo_urls: data?.photoUrls || [],
        notes: data?.notes || null,
        created_by: user?.id
      };

      const { data: record, error } = await supabase?.from('tools_accessories')?.insert(recordData)?.select()?.single();

      if (error) throw error;

      return this.convertToCamelCase(record);
    } catch (error) {
      console.error('Error creating tools/accessories record:', error);
      throw error;
    }
  },

  /**
   * Create multiple records in batch
   * @param {Array<Object>} records - Array of record data
   * @returns {Promise<Array<Object>>} - Created records
   */
  async createBatch(records) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      const recordsData = records?.map(record => ({
        work_order_id: record?.workOrderId,
        category: record?.category,
        item_name: record?.itemName,
        quantity: record?.quantity || 1,
        photo_urls: record?.photoUrls || [],
        notes: record?.notes || null,
        created_by: user?.id
      }));

      const { data, error } = await supabase?.from('tools_accessories')?.insert(recordsData)?.select();

      if (error) throw error;

      return data?.map(record => this.convertToCamelCase(record));
    } catch (error) {
      console.error('Error creating tools/accessories batch:', error);
      throw error;
    }
  },

  /**
   * Get all records for a work order
   * @param {string} workOrderId - Work order ID
   * @returns {Promise<Array<Object>>} - Records
   */
  async getByWorkOrder(workOrderId) {
    try {
      const { data, error } = await supabase?.from('tools_accessories')?.select('*')?.eq('work_order_id', workOrderId)?.order('category', { ascending: true })?.order('created_at', { ascending: true });

      if (error) throw error;

      return data?.map(record => this.convertToCamelCase(record)) || [];
    } catch (error) {
      console.error('Error fetching tools/accessories:', error);
      throw error;
    }
  },

  /**
   * Update record
   * @param {string} id - Record ID
   * @param {Object} updates - Update data
   * @returns {Promise<Object>} - Updated record
   */
  async update(id, updates) {
    try {
      const updateData = {};
      if (updates?.quantity !== undefined) updateData.quantity = updates?.quantity;
      if (updates?.photoUrls !== undefined) updateData.photo_urls = updates?.photoUrls;
      if (updates?.notes !== undefined) updateData.notes = updates?.notes;

      const { data, error } = await supabase?.from('tools_accessories')?.update(updateData)?.eq('id', id)?.select()?.single();

      if (error) throw error;

      return this.convertToCamelCase(data);
    } catch (error) {
      console.error('Error updating tools/accessories record:', error);
      throw error;
    }
  },

  /**
   * Delete record
   * @param {string} id - Record ID
   */
  async delete(id) {
    try {
      const { error } = await supabase?.from('tools_accessories')?.delete()?.eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting tools/accessories record:', error);
      throw error;
    }
  },

  /**
   * Convert snake_case to camelCase
   */
  convertToCamelCase(record) {
    if (!record) return null;

    return {
      id: record?.id,
      workOrderId: record?.work_order_id,
      category: record?.category,
      itemName: record?.item_name,
      quantity: record?.quantity,
      photoUrls: record?.photo_urls || [],
      notes: record?.notes,
      createdAt: record?.created_at,
      updatedAt: record?.updated_at,
      createdBy: record?.created_by
    };
  }
};